import { DEFAULT_DPI } from './generateFlowcode.constant'
import { FcGeneratorOptions, optionsDefaults } from '@dtx-company/flowcode-generator-types'
import { fcGenerateFlowcode } from './fcGenerateFlowcode'
import { generateFlowcode } from './generateFlowcode'
import { logger } from '@dtx-company/logger'
import { useEffect, useRef } from 'react'
import { useObjectURL } from './hooks/useObjectUrl'

export interface FlowcodeProps {
  alt?: string
  containerCx?: string
  onChange?: () => void
  options: FcGeneratorOptions
  renderLoadingIcon?: () => JSX.Element
  staticOptimization?: boolean // use only for fixed code previews
  onContextMenu?: () => void
  fallbackImageURL?: string
  onLoad?: () => void
}

export const Flowcode = ({
  alt = 'Flowcode',
  staticOptimization = false,
  options,
  renderLoadingIcon,
  onChange,
  onContextMenu,
  containerCx,
  fallbackImageURL = '',
  onLoad
}: FlowcodeProps): JSX.Element => {
  // when staticOptimization = true, used with a renderLoadingIcon which returns
  // a static image of a flowcode, we can defer loading the full
  // generator until a logoImageUrl with a blob: protocol is referenced
  const loadGenerator = useRef(!(renderLoadingIcon && staticOptimization))
  const { objectURL, setObject } = useObjectURL()
  const targetSize = options?.targetSize || optionsDefaults.targetSize
  const logoImageUrl = options?.logoImageUrl

  useEffect(() => {
    let isActive = true
    if (logoImageUrl?.startsWith('blob:')) {
      loadGenerator.current = true
    }

    async function generateFlowCode(): Promise<void> {
      try {
        const optionsString = JSON.stringify(options)
        const generatorOptions: FcGeneratorOptions = JSON.parse(optionsString)
        const result = await fcGenerateFlowcode(generatorOptions)
        if (!isActive) return
        result.setViewBox(`0 0 ${targetSize} ${targetSize}`)
        const blob = new Blob([result.getDom()], { type: 'image/svg+xml' })
        setObject(blob)
        onChange?.()
      } catch {
        const result = await generateFlowcode(options || {}, DEFAULT_DPI, 'png')
        const blob = await result.blob()
        setObject(blob)
        onChange?.()
      }
    }
    if (loadGenerator.current) {
      generateFlowCode().catch(e => {
        logger.logError(e, { technicalArea: 'codes' })
      })
    }
    return () => {
      isActive = false
    }
  }, [logoImageUrl, targetSize, onChange, options, setObject])

  if (!objectURL && !fallbackImageURL)
    return renderLoadingIcon ? renderLoadingIcon() : <div className={containerCx} />

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      alt={alt}
      className={containerCx}
      src={objectURL || fallbackImageURL}
      style={{ width: '100%', height: '100%' }}
      onContextMenu={onContextMenu}
      onLoad={() => {
        objectURL && URL.revokeObjectURL(objectURL)
        onLoad?.()
      }}
    />
  )
}
